import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminPlans } from "@/hooks/use-admin-plans";
import { useOverridePlan } from "@/hooks/use-admin-subscriptions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, MoreVertical } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  plan_name: string | null;
  plan_tier: string | null;
  subscription_status: string | null;
  subscription_id: string | null;
  created_at: string;
}

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<{ users: AdminUser[]; total: number }>({
    queryKey: ["admin-users", search, page],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: { action: "list_users", search, page, limit: 20 },
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    staleTime: 30 * 1000,
  });

  const { data: plans } = useAdminPlans();
  const overridePlanMutation = useOverridePlan();

  const [assignDialog, setAssignDialog] = useState<AdminUser | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState("");

  const handleAssignPlan = () => {
    if (!assignDialog?.subscription_id || !selectedPlanId) return;
    overridePlanMutation.mutate(
      { subscription_id: assignDialog.subscription_id, plan_id: selectedPlanId },
      {
        onSuccess: () => {
          setAssignDialog(null);
          setSelectedPlanId("");
        },
      }
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Usuarios</h2>
        <p className="text-muted-foreground">
          Lista de todos os usuarios do sistema
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-medium">Buscar</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por email ou nome..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Plano</TableHead>
                      <TableHead className="hidden sm:table-cell">Status</TableHead>
                      <TableHead className="hidden sm:table-cell">Criado em</TableHead>
                      <TableHead className="text-right">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.users ?? []).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{user.full_name || "-"}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-none">
                              {user.email}
                            </div>
                            {/* Show status inline on mobile */}
                            {user.subscription_status && (
                              <div className="sm:hidden mt-1">
                                <Badge
                                  variant={
                                    user.subscription_status === "active"
                                      ? "default"
                                      : user.subscription_status === "trialing"
                                      ? "secondary"
                                      : "destructive"
                                  }
                                  className="text-[10px]"
                                >
                                  {user.subscription_status}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.plan_name ? (
                            <Badge variant="outline">{user.plan_name}</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {user.subscription_status ? (
                            <Badge
                              variant={
                                user.subscription_status === "active"
                                  ? "default"
                                  : user.subscription_status === "trialing"
                                  ? "secondary"
                                  : "destructive"
                              }
                            >
                              {user.subscription_status}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {format(new Date(user.created_at), "dd/MM/yyyy", { locale: ptBR })}
                        </TableCell>
                        <TableCell className="text-right">
                          {/* Desktop: inline button */}
                          <div className="hidden sm:block">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setAssignDialog(user)}
                              disabled={!user.subscription_id}
                            >
                              Atribuir Plano
                            </Button>
                          </div>
                          {/* Mobile: dropdown */}
                          <div className="sm:hidden">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-10 w-10">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setAssignDialog(user)}
                                  disabled={!user.subscription_id}
                                >
                                  Atribuir Plano
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data?.users ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Nenhum usuario encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {data && data.total > 20 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground self-center">
                    Pagina {page} de {Math.ceil(data.total / 20)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.ceil(data.total / 20)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Proxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Assign Plan Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Atribuir Plano</DialogTitle>
            <DialogDescription>
              Altere o plano do usuario {assignDialog?.full_name || assignDialog?.email}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Plano</Label>
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Selecione um plano" />
              </SelectTrigger>
              <SelectContent>
                {(plans ?? []).map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name} ({plan.tier})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAssignDialog(null)} className="w-full sm:w-auto">
              Voltar
            </Button>
            <Button
              onClick={handleAssignPlan}
              disabled={!selectedPlanId || overridePlanMutation.isPending}
              className="w-full sm:w-auto"
            >
              {overridePlanMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
